import { gql } from "lib/apolloClient";
import {
  courseCalendarDetailFragment,
  calendarEventDetailFragment,
  calendarEventFragment,
  courseItemFragment,
  calendarEventMetaFragment,
  basicUserFragment,
  calendarEventAssignedDataFragment,
} from "./MyCalendarFragments";
import { fragments } from "SchoolCalendar/modules/CalendarFragment";
import {
  deadlineFragments,
  projectGroupFragment,
} from "Projects/modules/ProjectFragments";

export const getCourseCalendarDetailsQuery = gql`
  query getCourseCalendarDetails(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: CourseFilter
    $enabledModulesFilters: EnabledModulesFiltersInput
    $aclControlParameterFilter: ACLControlParameterFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
      ... on Student {
        ...studentItem
      }
    }
  }
  ${courseCalendarDetailFragment.staffItem}
  ${courseCalendarDetailFragment.studentItem}
`;

export const getCalendarOtherCoursesDetailQuery = gql`
  query getCalendarOtherCoursesDetail(
    $id: ID!
    $filters: CourseFilterInput
    $enabledModulesFilters: EnabledModulesFiltersInput
    $aclControlParameterFilter: ACLControlParameterFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          edges {
            node {
              ...courseItem
            }
          }
        }
      }
    }
  }
  ${courseItemFragment.courseItem}
`;

export const getCalendarEventsQuery = gql`
  query getCalendarEvents(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
    $statusWiseProjectFilters: StatusWiseProjectFilters
    $enabledModulesFilters: EnabledModulesFiltersInput
    $aclControlParameterFilter: ACLControlParameterFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
      ... on Student {
        ...studentItem
      }
    }
  }
  ${calendarEventFragment.studentItem}
  ${calendarEventFragment.staffItem}
`;

export const getCalendarEventsDataQuery = gql`
  query getCalendarEventsDataQuery(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
      ... on Student {
        ...studentItem
      }
    }
  }
  ${calendarEventMetaFragment.studentItem}
  ${calendarEventMetaFragment.staffItem}
`;

export const getCalendarEventsParticipantDataQuery = gql`
  query getCalendarEventsParticipantDataQuery(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
    $associatedEntitiesFilters: AssociatedEntitiesFilters!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
    }
  }
  ${calendarEventAssignedDataFragment.staffItem}
`;

export const getCalendarEventDetailsQuery = gql`
  query getCalendarEventDetails(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $statusWiseProjectFilters: StatusWiseProjectFilters
    $calendarUserType: String
    $associatedEntitiesFilters: AssociatedEntitiesFilters
    $organizationId: ID!
    $skipAssociatedEntities: Boolean! = false
    $skipUserDetails: Boolean! = false
  ) {
    node(id: $id, type: $type) {
      id
      ... on PlatformCalendarEvent {
        id
        startTime
        endTime
        calendars {
          id
          parentType
          parent {
            ... on Course {
              ...courseItem
            }
          }
        }
        itemType
        item(calendarUserType: $calendarUserType) {
          id
          ... on Assignment {
            id
            publishedAt
            visibility
            closeSubmissionDate
            isStudentSubmissionEnable: isStudentSubmissionEnabled
            isPrivate
            stringDeadline: deadline
            createdBy {
              ...staffItem
            }
            assignedStudents {
              id
              student {
                id
                ...studentItem @skip(if: $skipUserDetails)
              }
              submission {
                statusV2
                submittedAt
              }
              lockingState {
                state
              }
            }
            summaryCounts {
              unlockedSubmissions
              __typename
            }
            counts {
              totalAssignedStudents
              totalSubmissions
              totalDraftSubmissions
            }
            state {
              state
              publishedAt
              updatedAt
            }
            contentType
            content {
              id
              ... on Assessment {
                id
                title {
                  id
                  value
                }
                academicTerm {
                  id
                  lockingStatus
                }
                assessmentType {
                  id
                  value
                }
                lockingInfo {
                  isLocked
                }
              }
              ... on AssignmentResource {
                id
                label
                assignmentTitle: title
                meeting {
                  ...meetingItem
                }
              }
            }
            isFixedStartTime
          }
          ... on CalendarEventItem {
            id
            label
            isFullDayEvent
            start: startDateTime
            end: endDateTime
            eventType
            location
            isFullDayEvent
            description
            tag: eventTag {
              id
              label
              colorCode
              uid
            }
            createdBy {
              ...staffItem
            }
            meeting {
              ...meetingItem
            }
            repeatEventSettings: recurringSetting {
              frequency
              weekDays
              interval
              endDate
              monthDays
              setPositions
              count
              timezone
            }
            metadata {
              skipNonOperationalDays
              recurrenceType
            }
            associatedEntities(filters: $associatedEntitiesFilters)
              @skip(if: $skipAssociatedEntities) {
              id
              ... on Grade {
                id
                name
                curriculumProgram {
                  id
                  label
                  acronym
                }
              }
              ... on Course {
                id
                title
                grades {
                  id
                  name
                }
              }
            }
            sharedWith
            attachments {
              id
              type
              name
              url
              metadata
              displaySequence
              mimeType
            }
            eventReminders {
              reminderOffset
              channels
              triggerDayOffset
              triggerTimeOffset
            }
            users @skip(if: $skipAssociatedEntities) {
              id
              type
              ...userDetailItem @skip(if: $skipUserDetails)
            }
          }
          ... on CalendarEvent {
            id
            start: startDate
            end: endDate
            title
            location
            description
            grades
            tag {
              id
              label
              colorCode
              uid
            }
            author {
              id
              firstName
              lastName
              middleName
            }
          }
          ... on ProjectDeadline {
            ...projectDeadline
          }
          ... on ProjectGroupDeadline {
            ...deadlineDetails
          }
          ... on ProjectGroupTask {
            ...projectGroupTaskItem
          }
          ... on ProjectTaskResponse {
            ...projectTaskItem
          }
        }
      }
    }
  }

  ${calendarEventDetailFragment.courseItem}
  ${calendarEventDetailFragment.staffItem}
  ${calendarEventDetailFragment.studentItem}
  ${calendarEventDetailFragment.meetingItem}
  ${deadlineFragments.calendarDeadlineDetailsFragment}
  ${deadlineFragments.calendarProjectDeadlineFragment}
  ${deadlineFragments.calendarProjectGroupTaskFragment}
  ${deadlineFragments.calendarProjectTaskFragment}
  ${basicUserFragment.userDetailItem}
`;

export const getCalendarEventsTrimmedQuery = gql`
  query getCalendarEventsTrimmed(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        calendarEvents(filters: $filters) {
          id
          startTime
          endTime
        }
      }
      ... on Student {
        id
        calendarEvents(filters: $filters) {
          id
          startTime
          endTime
        }
      }
    }
  }
`;

export const getSchoolCalendarEventsQuery = gql`
  query getCalendarEvents(
    $rangeBeginStartDate: String!
    $rangeEndStartDate: String!
    $filters: CalendarEventFilter
  ) {
    school {
      calendarEvents(
        rangeBeginStartDate: $rangeBeginStartDate
        rangeEndStartDate: $rangeEndStartDate
        filters: $filters
      ) {
        ...calendarEvent
      }
    }
  }
  ${fragments.calendarEvent}
`;

export const getPlatformCalendarEventItemsQuery = gql`
  query getPlatformCalendarEventItems(
    $rangeBeginStartDate: String!
    $rangeEndStartDate: String!
    $filters: CalendarEventFilter
  ) {
    platform {
      calendarEventItems(
        rangeBeginStartDate: $rangeBeginStartDate
        rangeEndStartDate: $rangeEndStartDate
        filters: $filters
      ) {
        id
        start: startDateTime
        end: endDateTime
        title: label
      }
    }
  }
`;

export const getUserProjectGroupYearGroupsQuery = gql`
  query getUserProjectGroupYearGroups(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserProjectGroupFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        projectGroups(filters: $filters) {
          ...projectGroupInfoBasicDetails
        }
      }

      ... on Student {
        id
        projectGroups(filters: $filters) {
          ...projectGroupInfoBasicDetails
        }
      }
    }
  }
  ${projectGroupFragment.projectGroupInfoBasicDetails}
`;

export const getCalendarUidQuery = gql`
  query getCalendarUids($id: ID!, $type: ENTITY_TYPE_ENUM!, $studentId: ID) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        calendars {
          type
          uid
        }
        __typename
      }
      ... on Student {
        calendars {
          type
          uid
        }
        __typename
      }
      ... on FamilyMember {
        calendars(studentId: $studentId) {
          type
          uid
        }
        __typename
      }
      __typename
    }
  }
`;

export const getOrganizationDetailsQuery = gql`
  query getOrganizationDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        grades {
          id
          name
          studentCount
          displaySequence
        }
      }
    }
  }
`;

export const getCalendarEventAssociatedEntitiesQueryV2 = gql`
  query getCalendarEventAssociatedEntities(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $associatedEntitiesFilters: AssociatedEntitiesFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on PlatformCalendarEvent {
        id
        item {
          id
          ... on CalendarEventItem {
            id
            associatedEntities(filters: $associatedEntitiesFilters) {
              id
              ... on Grade {
                id
              }
              ... on Course {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getCalendarEventAssociatedEntitiesQuery = gql`
  query getCalendarEventAssociatedEntities(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $associatedEntitiesFilters: AssociatedEntitiesFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on PlatformCalendarEvent {
        id
        item {
          id
          ... on CalendarEventItem {
            id
            associatedEntities(filters: $associatedEntitiesFilters) {
              id
              ... on Grade {
                id
                name
                staffCount
                studentCount
                familyMemberCount
                curriculumProgram {
                  id
                  label
                  acronym
                }
              }
              ... on Course {
                id
                title
                grades {
                  id
                  name
                }
                students {
                  totalCount
                }
                collaborator {
                  totalCount
                }
                familyMembers {
                  totalCount
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserRnpRolesQuery = gql`
  query getUserRnpRoles(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: RnPRoleContextDetailFilterInput
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        rnpRoleContextDetail(filters: $filters) {
          id
          contextType
          role {
            id
            isDefaultRole
            globalRole {
              id
              uid
            }
          }
        }
      }
    }
  }
`;
